import { Component, Input, EventEmitter, Output } from '@angular/core';
import { LawFirm, LawFirmEmployeeType } from '@models/lawFirms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { LawFirmSearchState } from '@state/algolia-search/law-firms-search';
import { LawFirmSearchDispatch } from '@state/algolia-search/law-firms-search/dispatch';
import {
	LawyerSearchModels,
	LawyerSearchState,
} from '@state/algolia-search/lawyer-search';
import { LawyerSearchDispatch } from '@state/algolia-search/lawyer-search/dispatch';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { CreateOrUpdateLawFirmModel } from '../../create/create.component';
import { TestHooks } from './search-firm-side-form.hooks';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';

export enum DropdownSelect {
	SELECTLAWFIRM = 'SelectLawFirm',
	SELECTLAWYER = 'SelectLawyer',
	SELECTCLERK = 'SelectClerk',
}

@UntilDestroy()
@Component({
	selector: 'app-law-firm-search-side-form',
	templateUrl: './search-firm-side-form.component.html',
	standalone: false,
})
export class LawFirmSearchSideFormComponent {
	testHooks = TestHooks;
	public showLawFirmsSearchResult = false;
	public lawFirmsLoading = false;
	public loading = false;
	public ICON = ICON;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public lawFirmName = '';
	public matterId;
	public dropdownSelect = DropdownSelect;
	public lawyers = [];
	public clerks = [];
	@Input() label;
	@Input() placeholder;
	@Input() skeletonLoading = false;
	@Input() showClerk;
	@Input() showAiIcon = false;

	@Output() onSelectedLawfirm = new EventEmitter<LawFirm>();
	@Output() onCreateLawfirm = new EventEmitter<CreateOrUpdateLawFirmModel>();
	@Output() onSelectedLawyer = new EventEmitter<string>();
	@Output() onSelectedClerk = new EventEmitter<string>();

	@Select(SelectedMatter.id) matterId$: Observable<string>;
	@Select(LawFirmSearchState.getList) lawFirms$: Observable<LawFirm[]>;
	@Select(LawFirmSearchState.getSearching)
	searchingLawFirms$: Observable<boolean>;

	@Select(LawyerSearchState.getList) lawyers$: Observable<
		LawyerSearchModels.LayerSearchResult[]
	>;
	@Select(LawyerSearchState.getSearching)
	searchingLawyers$: Observable<boolean>;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	constructor(
		private lawyerSearchDispatch: LawyerSearchDispatch,
		private lawfirmSearchDispatch: LawFirmSearchDispatch,
	) {
		this.matterId$
			.pipe(untilDestroyed(this))
			.subscribe((value) => (this.matterId = value));
		this.lawyers$.pipe(untilDestroyed(this)).subscribe((value) => {
			this.lawyers = value.filter((employee) => employee.role === 'lawyer');
			this.clerks = value.filter((employee) => employee.role === 'clerk');
		});
	}

	searchEmployees(employeeSearchTerm: string) {
		if (this.showClerk) {
			this.lawyerSearchDispatch.Search({ searchValue: employeeSearchTerm });
		} else {
			this.lawyerSearchDispatch.Search({
				searchValue: employeeSearchTerm,
				lawFirmId: '',
				role: LawFirmEmployeeType.LAWYER,
			});
		}
	}

	searchLawFirms(lawFirmSearchTerm) {
		this.showLawFirmsSearchResult = true;
		this.loading = false;
		this.lawfirmSearchDispatch.Search(lawFirmSearchTerm);
	}

	search = ($event) => {
		this.loading = true;
		this.searchLawFirms($event);
		//if (!this.isLeapMatter) {
		this.searchEmployees($event);
		//}
	};

	createLawFirm() {
		const lawFirmModel: CreateOrUpdateLawFirmModel = {
			name: this.lawFirmName,
			lawFirm: null,
		};
		this.onCreateLawfirm.emit(lawFirmModel);
	}

	onSelect = (ev: DropdownResult<string | LawFirm>) => {
		if (ev.groupType === DropdownSelect.SELECTLAWFIRM) {
			this.onSelectedLawfirm.emit(ev.result as LawFirm);
		} else if (ev.groupType === DropdownSelect.SELECTLAWYER) {
			this.onSelectedLawyer.emit(ev.result as string);
		} else if (ev.groupType === DropdownSelect.SELECTCLERK) {
			this.onSelectedClerk.emit(ev.result as string);
		}
	};
}
